import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { contracts } from '../../../../shared/features/platform.contracts'
import { BRANDING_CONFIG_CHANGED_EVENT } from '../publicAssets'
import { publicApiFetch } from '../services/apiClient'

const DEFAULTS = {
  name: 'GAEZLA',
  subtitle: 'Console',
  tagline: 'Investigate, execute, <strong>and review</strong>',
  taglineSub: 'Governed operations for the estate you run.',
  loginHeading: 'Welcome back.<br /><strong>Sign in to continue.</strong>',
  loginSub: 'Authenticate with your organisation account to access the console.',
  accentColor: null,
  licensing: {
    mode: 'auto',
    pollSeconds: 120,
    showStateBadge: false,
  },
}

const DISPLAY_KEYS = ['name', 'subtitle', 'tagline', 'taglineSub', 'loginHeading', 'loginSub']
const SAFE_HTML_TAG_RE = /<\/?strong>|<br\s*\/?>/gi
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/
const ACCENT_PROPERTIES = ['--cui-primary', '--cui-primary-rgb', '--login-globe-color', '--login-btn-bg']

const BrandingContext = createContext(DEFAULTS)

function safeDisplayConfig(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {}
  const result = {}
  for (const key of DISPLAY_KEYS) {
    const value = input[key]
    if (typeof value !== 'string' || value.trim() === '') continue
    if ((key === 'tagline' || key === 'loginHeading')
      && /[<>]/.test(value.replace(SAFE_HTML_TAG_RE, ''))) continue
    result[key] = value
  }
  if (typeof input.accentColor === 'string' && HEX_COLOR_RE.test(input.accentColor)) {
    result.accentColor = input.accentColor
  } else if (input.accentColor === null) {
    result.accentColor = null
  }
  return result
}

function safeLicensing(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return DEFAULTS.licensing
  return {
    mode: typeof input.mode === 'string' ? input.mode : DEFAULTS.licensing.mode,
    pollSeconds: Number.isFinite(input.pollSeconds) && input.pollSeconds > 0
      ? input.pollSeconds
      : DEFAULTS.licensing.pollSeconds,
    showStateBadge: typeof input.showStateBadge === 'boolean'
      ? input.showStateBadge
      : DEFAULTS.licensing.showStateBadge,
  }
}

function mergeBranding(snapshot, runtime) {
  return {
    ...DEFAULTS,
    ...safeDisplayConfig(snapshot),
    ...safeDisplayConfig(runtime),
    licensing: safeLicensing(snapshot?.licensing),
  }
}

function hexToRgb(hex) {
  const h = hex.replace('#', '')
  const n = parseInt(h, 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

function applyAccentColor(hex) {
  const root = document.documentElement
  if (!hex) {
    for (const property of ACCENT_PROPERTIES) root.style.removeProperty(property)
    return
  }
  root.style.setProperty('--cui-primary', hex)
  root.style.setProperty('--cui-primary-rgb', hexToRgb(hex))
  root.style.setProperty('--login-globe-color', hexToRgb(hex))
  root.style.setProperty('--login-btn-bg', hex)
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS)
  const snapshotRef = useRef(null)
  const runtimeRef = useRef(null)

  useEffect(() => {
    let mounted = true
    const apply = () => {
      if (!mounted) return
      const merged = mergeBranding(snapshotRef.current, runtimeRef.current)
      setBranding(merged)
      applyAccentColor(merged.accentColor)
      document.title = `${merged.name} ${merged.subtitle}`
    }

    const loadSnapshot = async () => {
      try {
        const response = await fetch('/branding.json')
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        snapshotRef.current = await response.json()
        apply()
      } catch {
        // The static file is an optional first-paint/outage snapshot.
      }
    }

    const loadRuntime = async () => {
      try {
        const response = await publicApiFetch(contracts.publicBrandingConfig.build())
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        runtimeRef.current = await response.json()
        apply()
      } catch {
        // Keep the static snapshot/defaults when the authoritative Worker is unavailable.
      }
    }

    loadSnapshot()
    loadRuntime()
    window.addEventListener(BRANDING_CONFIG_CHANGED_EVENT, loadRuntime)
    return () => {
      mounted = false
      window.removeEventListener(BRANDING_CONFIG_CHANGED_EVENT, loadRuntime)
    }
  }, [])

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBranding() {
  return useContext(BrandingContext)
}
